import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSeedling } from '@fortawesome/pro-light-svg-icons';
import {
  faSquarePlus,
  faPencil,
  faTrash,
  faInfoCircle,
  faClose
} from '@fortawesome/pro-regular-svg-icons';
import { updatePartialProfileGeneralInfo } from 'clients/internal/profileService';
import { Flexbox, Text } from '@morressier/ts';
import { ExitModal, LinkButton } from 'components/ProfileUIComponents';
import { KeyedMutator } from 'swr';
import { IProfile } from 'typings/profile';
import CareerStageModal from './CareerStageModal';
import {
  Container,
  MissingCareerStageIcon,
  Actions,
  CareerStageTooltip,
  InfoIcon,
  Heading
} from './style';
import { careerStageMapping } from '../constants';
import { logEvent } from 'utils/eventTracking';

interface ICareerStageProps {
  careerStage?: string;
  userId: string;
  isSameUser: boolean;
  mutateProfile: KeyedMutator<IProfile>;
}

const CareerStage = (props: ICareerStageProps) => {
  const { careerStage, userId, mutateProfile, isSameUser } = props;

  const [showCareerStageForm, setShowCareerStageForm] = useState(false);
  const openCareerStageForm = () => setShowCareerStageForm(true);
  const closeCareerStageForm = () => setShowCareerStageForm(false);

  const [showExitModal, setShowExitModal] = useState<boolean>(false);
  const closeExitModal = () => setShowExitModal(false);
  const openExitModal = () => setShowExitModal(true);

  const handleAddCareerStageClicked = () => {
    logEvent('PROFILE_CAREER_STAGE_ADD_CAREER_STAGE_CLICKED');
    openCareerStageForm();
  };

  const handleRemoveCareerStageClicked = () => {
    logEvent('PROFILE_REMOVE_CAREER_STAGE_CLICKED');
    openExitModal();
  };

  const handleEditCareerStageClicked = () => {
    logEvent('PROFILE_EDIT_CAREER_STAGE_EDIT_CLICKED');
    openCareerStageForm();
  };

  const handleExitModalConfirmAction = async () => {
    await updatePartialProfileGeneralInfo(userId, { careerStage: '' });
    mutateProfile();
    closeExitModal();
    logEvent('PROFILE_REMOVE_CAREER_STAGE_REMOVE_CAREER_STAGE_CLICKED');
  };

  const handleExitModalCancelAction = () => {
    logEvent('PROFILE_REMOVE_CAREER_STAGE_REMOVE_CAREER_STAGE_CANCELED');
    closeExitModal();
  };

  return (
    <div>
      <Heading size="h4_new" fontWeight="semiBold">
        Career stage
      </Heading>
      <Container
        gap="1rem"
        id="career-stage"
        isCareerStage={Boolean(careerStage)}
      >
        {!careerStage && <MissingCareerStageIcon icon={faSeedling} />}
        <div>
          <Flexbox gap="8px" alignItems="center">
            <Text fontWeight="bold">
              {careerStage
                ? careerStageMapping[careerStage]?.title
                : 'Add your career stage'}
            </Text>
            <CareerStageTooltip data-tool-tip="Based on the European Commission's Research Career Framework">
              <InfoIcon icon={faInfoCircle} />
            </CareerStageTooltip>
          </Flexbox>
          <Text>
            {careerStage
              ? careerStageMapping[careerStage]?.desc
              : 'Make profile visitors aware of your career stage progression and competencies.'}
          </Text>
          {isSameUser && (
            <Actions>
              {careerStage ? (
                <Flexbox gap="1rem">
                  <LinkButton onClick={handleEditCareerStageClicked}>
                    <FontAwesomeIcon icon={faPencil} /> Edit
                  </LinkButton>
                  <LinkButton onClick={handleRemoveCareerStageClicked}>
                    <FontAwesomeIcon icon={faTrash} /> Remove
                  </LinkButton>
                </Flexbox>
              ) : (
                <LinkButton onClick={handleAddCareerStageClicked}>
                  <FontAwesomeIcon icon={faSquarePlus} /> Add career stage
                </LinkButton>
              )}
            </Actions>
          )}
        </div>
        {showCareerStageForm && (
          <CareerStageModal
            defaultCareerStage={careerStage}
            isOpen={showCareerStageForm}
            onDismiss={closeCareerStageForm}
            mutateProfile={mutateProfile}
            userId={userId}
          />
        )}
        <ExitModal
          onConfirm={handleExitModalConfirmAction}
          onCancel={handleExitModalCancelAction}
          isOpen={showExitModal}
          onClose={closeExitModal}
          title="Remove career stage?"
          description="Your career stage will be permanently removed from your profile."
          icon={faClose}
          confirmActionText="Remove career stage"
          cancelActionText="Cancel"
        />
      </Container>
    </div>
  );
};
export default CareerStage;
